import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import FeatureGuard from './FeatureGuard';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { axiosInstance } from '../axiosInstans';

const FeatureFlagDebug: React.FC = () => {
    const {
        isLoaded,
        getAllFeatures,
        getAllConfigurations,
        refreshFeatures,
        isDoubleSignatureRequired,
    } = useFeatureFlags();

    const features = getAllFeatures();
    const configurations = getAllConfigurations();

    const testApiCall = async () => {
        try {
            console.log('🧪 Testing direct API call...');
            const response = await axiosInstance.get('/api/v1/settings/web');
            console.log('✅ Direct API call successful:', response.data);
            alert('API call successful! Check console for details.');
        } catch (error) {
            console.error('❌ Direct API call failed:', error);
            alert('API call failed! Check console for details.');
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                🚀 Feature Flags Debug
            </Typography>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Status</Typography>
                <Typography>Loaded: {isLoaded ? '✅ Yes' : '❌ No'}</Typography>
                <Typography>
                    Features count: {Object.keys(features).length}
                </Typography>
                <Typography>
                    Configurations count: {Object.keys(configurations).length}
                </Typography>

                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={refreshFeatures}>
                        🔄 Refresh Features
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={testApiCall}
                        color="secondary"
                    >
                        🧪 Test API Call
                    </Button>
                </Box>
            </Paper>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Features</Typography>
                <pre
                    style={{
                        fontSize: '12px',
                        overflow: 'auto',
                        maxHeight: '200px',
                    }}
                >
                    {JSON.stringify(features, null, 2)}
                </pre>
            </Paper>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Configurations</Typography>
                <pre
                    style={{
                        fontSize: '12px',
                        overflow: 'auto',
                        maxHeight: '200px',
                    }}
                >
                    {JSON.stringify(configurations, null, 2)}
                </pre>
            </Paper>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Double Signature Tests</Typography>
                <Typography>
                    Withdrawal:{' '}
                    {isDoubleSignatureRequired('withdrawal')
                        ? '✅ Required'
                        : '❌ Not required'}
                </Typography>
                <Typography>
                    Deposit:{' '}
                    {isDoubleSignatureRequired('deposit')
                        ? '✅ Required'
                        : '❌ Not required'}
                </Typography>
                <Typography>
                    Inventory:{' '}
                    {isDoubleSignatureRequired('inventory')
                        ? '✅ Required'
                        : '❌ Not required'}
                </Typography>
                <Typography>
                    Disposal:{' '}
                    {isDoubleSignatureRequired('disposal')
                        ? '✅ Required'
                        : '❌ Not required'}
                </Typography>
            </Paper>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">FeatureGuard Tests</Typography>

                <FeatureGuard featureKey={FEATURE_FLAGS.USER_MANAGEMENT_PAGE}>
                    <Box sx={{ p: 1, bgcolor: 'success.light', mb: 1 }}>
                        ✅ User Management Page is ENABLED
                    </Box>
                </FeatureGuard>

                <FeatureGuard
                    featureKey={FEATURE_FLAGS.USER_MANAGEMENT_PAGE}
                    fallback={
                        <Box sx={{ p: 1, bgcolor: 'error.light', mb: 1 }}>
                            ❌ User Management Page is DISABLED
                        </Box>
                    }
                >
                    <Box sx={{ p: 1, bgcolor: 'success.light', mb: 1 }}>
                        ✅ User Management Page is ENABLED
                    </Box>
                </FeatureGuard>

                <FeatureGuard
                    featureKey="non-existent-feature"
                    fallback={
                        <Box sx={{ p: 1, bgcolor: 'warning.light', mb: 1 }}>
                            ⚠️ Non-existent feature shows fallback (expected)
                        </Box>
                    }
                >
                    <Box sx={{ p: 1, bgcolor: 'error.light', mb: 1 }}>
                        ❌ This should not show
                    </Box>
                </FeatureGuard>
            </Paper>

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6">Ambulance Features Tests</Typography>

                <FeatureGuard
                    featureKey={FEATURE_FLAGS.AMBULANCE_BAG_TRANSACTION}
                    fallback={
                        <Box sx={{ p: 1, bgcolor: 'error.light', mb: 1 }}>
                            ❌ Uttag Amb.uppdrag is DISABLED
                        </Box>
                    }
                >
                    <Box sx={{ p: 1, bgcolor: 'success.light', mb: 1 }}>
                        ✅ Uttag Amb.uppdrag is ENABLED
                    </Box>
                </FeatureGuard>

                <FeatureGuard
                    featureKey={
                        FEATURE_FLAGS.AMBULANCE_BAG_INVENTORY_TRANSACTION
                    }
                    fallback={
                        <Box sx={{ p: 1, bgcolor: 'error.light', mb: 1 }}>
                            ❌ Inventering skiftbyte is DISABLED
                        </Box>
                    }
                >
                    <Box sx={{ p: 1, bgcolor: 'success.light', mb: 1 }}>
                        ✅ Inventering skiftbyte is ENABLED
                    </Box>
                </FeatureGuard>
            </Paper>
        </Box>
    );
};

export default FeatureFlagDebug;
